import {parseAxiosPromise} from '@/axiosInstance'
import {QUERY_KEYS} from '@/queryClient'
import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {captureException} from '@sentry/react'
import {httpGetDistricts} from './address.http'
import {District, PaginationResponse, ResponseType} from '@/types'
import {z} from 'zod'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'
import {useInputSearch} from '@/hooks/useInputSearch'

export const HttpGetDistrictsResponse = z.object({
    data: z.array(District),
    meta: PaginationResponse
})
export type HttpGetDistrictsResponse = z.infer<typeof HttpGetDistrictsResponse>

export const useGetDistricts = ({
    search,
    responseType,
    limit = 20,
    options
}: {
    search?: string
    responseType: ResponseType
    limit?: number
    options?: Pick<UseInfiniteQueryOptions<HttpGetDistrictsResponse>, 'enabled'>
}) => {
    const {searchValue, onSearch, onResetSearch} = useInputSearch()

    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.DISTRICTS, searchValue],
        queryFn: ({pageParam = 1}) =>
            parseAxiosPromise({
                axiosPromise: httpGetDistricts({
                    page: pageParam,
                    search: searchValue ?? search,
                    limit,
                    responseType
                }),
                responseShape: HttpGetDistrictsResponse,
                onZodError: captureException
            }),
        ...options,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : [],
        searchValue,
        onResetSearch,
        onSearch
    }
}
